import { EyeOutlined, HistoryOutlined, InboxOutlined, ProfileOutlined } from '@ant-design/icons';
import { Button, Empty, Popconfirm, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { type WorkflowSummary } from '@/api/workflows';
import { ICON_BTN_PROPS as iconBtn } from '@/components/iconButton';
import { WorkflowCard } from '@/components/WorkflowCard';
import { RejectionDetailsModal } from '@/originator/RejectionDetailsModal';

/**
 * Approver-side view of workflows currently in the Rejected state. Passive
 * record — the originator drives the resubmit flow. Card status tag opens
 * the shared RejectionDetailsModal so the approver can re-read what was
 * asked for. Reuses the same modal the originator sees; the payload comes
 * off the versions list, no bespoke endpoint needed.
 *
 * Scope decision (2026-07-15): "Currently-Rejected only" — once the
 * originator resubmits, the card moves back to the Pending Queue and drops
 * off this tab. Historical rejections live in Audit History.
 */
type Props = {
  items: WorkflowSummary[] | null;
  loading: boolean;
  /** Current search query — same pattern as sibling tabs; drives the empty-state copy. */
  searchQuery?: string;
  /** Callback the search-empty state calls to clear the search input. */
  onClearSearch?: () => void;
  /** Retire the workflow. Rejected is one of the two archivable statuses. */
  onArchive?: (id: string) => void | Promise<void>;
};

export function RejectedWorkflows({
  items,
  loading,
  searchQuery = '',
  onClearSearch,
  onArchive,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rejectionTarget, setRejectionTarget] = useState<WorkflowSummary | null>(null);

  if (loading && items === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  const visible = (items ?? []).filter((w) => w.status === 'Rejected');

  if (visible.length === 0) {
    const needle = searchQuery.trim();
    return (
      <Empty
        description={
          needle
            ? t('workflow.list.noneMatchSearch', {
                query: needle,
                defaultValue: 'No workflows match "{{query}}" on this tab.',
              })
            : t('approver.rejected.empty')
        }
      >
        {needle && onClearSearch && (
          <Button onClick={onClearSearch}>
            {t('workflow.list.clearSearch', { defaultValue: 'Clear search' })}
          </Button>
        )}
      </Empty>
    );
  }

  const versionForCard = (w: WorkflowSummary) =>
    // Prefer the in-flight version label ("v2 Rejected") over the historical
    // approved baseline, mirroring the Originator card's Rejected view.
    w.inProgressVersionLabel || w.currentVersionLabel || 'v1';

  const grid: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
  };

  return (
    <>
      <div style={grid}>
        {visible.map((w) => (
          <WorkflowCard
            key={w.id}
            workflow={w}
            versionLabel={versionForCard(w)}
            status="Rejected"
            onStatusClick={() => setRejectionTarget(w)}
            actions={
              <>
                <Tooltip title={t('workflow.builder.preview')}>
                  <Button
                    {...iconBtn}
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/approver/workflows/${w.id}/preview`)}
                  />
                </Tooltip>
                <Tooltip title={t('workflow.list.viewDetails')}>
                  <Button
                    {...iconBtn}
                    icon={<ProfileOutlined />}
                    onClick={() => navigate(`/approver/workflows/${w.id}`)}
                  />
                </Tooltip>
                <Tooltip title={t('workflow.list.versions')}>
                  <Button
                    {...iconBtn}
                    icon={<HistoryOutlined />}
                    onClick={() => navigate(`/approver/workflows/${w.id}/versions`)}
                  />
                </Tooltip>
                <Popconfirm
                  title={t('workflow.archive.archiveTitle', {
                    defaultValue: 'Archive this workflow?',
                  })}
                  description={t('workflow.archive.archiveConfirm', {
                    defaultValue:
                      'It leaves the dashboard but is not deleted. You can restore it from the Archive page.',
                  })}
                  okText={t('workflow.archive.archive', { defaultValue: 'Archive' })}
                  cancelText={t('common.cancel', { defaultValue: 'Cancel' })}
                  onConfirm={() => onArchive?.(w.id)}
                >
                  <Tooltip title={t('workflow.archive.archive', { defaultValue: 'Archive' })}>
                    <Button {...iconBtn} icon={<InboxOutlined />} />
                  </Tooltip>
                </Popconfirm>
              </>
            }
          />
        ))}
      </div>
      <RejectionDetailsModal workflow={rejectionTarget} onClose={() => setRejectionTarget(null)} />
    </>
  );
}
